import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/nexus-ui/NexusToast';
import CommandBar from './components/instrument/CommandBar';
import Landing from './pages/Landing';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminGPTConfig from './pages/admin/AdminGPTConfig';
import AdminKnowledge from './pages/admin/AdminKnowledge';
import AdminTenants from './pages/admin/AdminTenants';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminAuditLogs from './pages/admin/AdminAuditLogs';
import AdminBilling from './pages/admin/AdminBilling';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminCustomerDetail from './pages/admin/AdminCustomerDetail';
import AdminCustomerInsights from './pages/admin/AdminCustomerInsights';

// Portal Pages
import PortalLogin from './pages/portal/PortalLogin';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalKnowledge from './pages/portal/PortalKnowledge';
import PortalChat from './pages/portal/PortalChat';
import PortalAPIKeys from './pages/portal/PortalAPIKeys';
import PortalSettings from './pages/portal/PortalSettings';
import PortalBilling from './pages/portal/PortalBilling';
import PortalAnalytics from './pages/portal/PortalAnalytics';
import PortalTools from './pages/portal/PortalTools';
import PortalAssets from './pages/portal/PortalAssets';
import PortalPricing from './pages/portal/PortalPricing';
import PortalUsage from './pages/portal/PortalUsage';
import PortalRegister from './pages/portal/PortalRegister';
import PortalActivate from './pages/portal/PortalActivate';
import { PortalForgotPassword, PortalResetPassword } from './pages/portal/PortalPassword';
import PortalTeam from './pages/portal/PortalTeam';
import PortalOrders from './pages/portal/PortalOrders';
import PortalDesignStudio from './pages/portal/PortalDesignStudio';
import VerifyInvoice from './pages/VerifyInvoice';
import Pricing from './pages/Pricing';

// Consumer Pages
import ConsumerChat from './pages/consumer/ConsumerChat';
import PublicDesignView from './pages/PublicDesignView';

// Layouts
import AdminLayout from './components/layout/AdminLayout';
import PortalLayout from './components/layout/PortalLayout';

// Auth guards
import { AdminAuthGuard, PortalAuthGuard } from './components/auth/AuthGuards';
import { usePortalStore } from './stores';

import { ThemeProvider } from './hooks/useThemeToggle';

// Tiny route gate — redirects non-admin org members away from
// admin-only pages (Team, KB management). Super-admins always pass.
const RequireOrgAdmin = ({ children }) => {
  const { user } = usePortalStore();
  const isAdmin = (user?.role === 'admin') || !!user?.is_super_admin;
  if (!isAdmin) return <Navigate to="/portal/dashboard" replace />;
  return children;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5000,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <div className="min-h-screen bg-nexus-bg-primary text-nexus-text-primary transition-colors duration-300">
          <Routes>
            {/* Landing */}
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/verify" element={<VerifyInvoice />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminAuthGuard><AdminLayout /></AdminAuthGuard>}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="gpt-config" element={<AdminGPTConfig />} />
              <Route path="knowledge" element={<AdminKnowledge />} />
              <Route path="tenants" element={<AdminTenants />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
              <Route path="billing" element={<AdminBilling />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="customers/insights" element={<AdminCustomerInsights />} />
              <Route path="customers/:tenantId" element={<AdminCustomerDetail />} />
            </Route>
            
            {/* Portal Routes */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/register" element={<PortalRegister />} />
            <Route path="/portal/activate" element={<PortalActivate />} />
            <Route path="/portal/forgot-password" element={<PortalForgotPassword />} />
            <Route path="/portal/reset-password" element={<PortalResetPassword />} />
            <Route path="/portal/:tenantSlug/login" element={<PortalLogin />} />
            <Route path="/portal/:tenantSlug/register" element={<PortalLogin />} />
            <Route path="/portal" element={<PortalAuthGuard><PortalLayout /></PortalAuthGuard>}>
              <Route index element={<Navigate to="/portal/dashboard" replace />} />
              <Route path="dashboard" element={<PortalDashboard />} />
              <Route path="knowledge" element={<PortalKnowledge />} />
              <Route path="chat" element={<PortalChat />} />
              <Route path="chat/:conversationId" element={<PortalChat />} />
              <Route path="api-keys" element={<PortalAPIKeys />} />
              <Route path="billing" element={<PortalBilling />} />
              <Route path="analytics" element={<PortalAnalytics />} />
              <Route path="settings" element={<PortalSettings />} />
              <Route path="tools" element={<PortalTools />} />
              <Route path="assets" element={<PortalAssets />} />
              <Route path="pricing" element={<PortalPricing />} />
              <Route path="usage" element={<PortalUsage />} />
              <Route path="team" element={<RequireOrgAdmin><PortalTeam /></RequireOrgAdmin>} />
              <Route path="orders" element={<PortalOrders />} />
              <Route path="design-studio" element={<PortalDesignStudio />} />
              <Route path="settings/team" element={<RequireOrgAdmin><PortalTeam /></RequireOrgAdmin>} />
            </Route>
            
            {/* Consumer Chat (public, no auth) */}
            <Route path="/c/:tenantSlug" element={<ConsumerChat />} />
            <Route path="/chat/:tenantSlug" element={<ConsumerChat />} />

            {/* Public Design Studio share (no auth) */}
            <Route path="/public/design/:token" element={<PublicDesignView />} />
            
            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CommandBar />
          <Toaster />
        </div>
      </BrowserRouter>
    </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
